import React, { useMemo } from "react";
import {
  Modal,
  Box,
  Typography,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import { tokens, withAlpha } from "../../Common/designTokens";
import { decodeFixMessage, getMessageTypeDescription } from "./FixMessageDecoder";
import { headerStyle, modalStyle } from "../OrderEntry/styles";

const FixMessageDecoderModal = ({ open, onClose, rawMessage, timestamp }) => {
  const parsedMessage = useMemo(() => {
    return decodeFixMessage(rawMessage);
  }, [rawMessage]);

  const messageType = useMemo(() => {
    return getMessageTypeDescription(parsedMessage);
  }, [parsedMessage]);

  const handleCopyRaw = () => {
    navigator.clipboard.writeText(rawMessage || "");
  };

  const handleCopyDecoded = () => {
    const text = parsedMessage
      .map((f) => `${f.tagName} (${f.tag}): ${f.displayValue}`)
      .join("\n");
    navigator.clipboard.writeText(text);
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby="fix-decoder-modal-title"
    >
      <Box
        sx={{
          ...modalStyle,
          width: "800px",
          maxWidth: "95vw",
          maxHeight: "90vh",
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header */}
        <Box sx={{ ...headerStyle, flexShrink: 0 }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography
              id="fix-decoder-modal-title"
              variant="h6"
              component="h2"
              sx={{ fontWeight: "bold" }}
            >
              FIX Message Decoder
            </Typography>
            <Chip
              label={messageType}
              size="small"
              sx={{
                backgroundColor: withAlpha(tokens.palette.primary, 0.1),
                color: tokens.palette.primary,
                fontWeight: 600,
              }}
            />
          </Box>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, overflow: "auto", p: 2 }}>
          {/* Raw Message Section */}
          <Box sx={{ mb: 3 }}>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Raw Message
              </Typography>
              <IconButton size="small" onClick={handleCopyRaw} title="Copy raw message">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <Box
              sx={{
                p: 1.5,
                backgroundColor: tokens.colors.defaultGridRowColor,
                borderRadius: "8px",
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                fontFamily: "monospace",
                fontSize: "12px",
                wordBreak: "break-all",
                maxHeight: "100px",
                overflow: "auto",
              }}
            >
              {rawMessage || "No message data"}
            </Box>
          </Box>

          {/* Decoded Fields Table */}
          <Box>
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                mb: 1,
              }}
            >
              <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                Decoded Fields ({parsedMessage.length.toLocaleString()})
              </Typography>
              <IconButton size="small" onClick={handleCopyDecoded} title="Copy decoded message">
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
            <TableContainer
              component={Paper}
              sx={{
                maxHeight: "400px",
                border: `1px solid ${tokens.colors.bodyBorderColor50}`,
                borderRadius: "8px",
              }}
            >
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        backgroundColor: tokens.colors.defaultGridRowColor,
                        width: "80px",
                      }}
                    >
                      Tag
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        backgroundColor: tokens.colors.defaultGridRowColor,
                        width: "200px",
                      }}
                    >
                      Field Name
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 700,
                        backgroundColor: tokens.colors.defaultGridRowColor,
                      }}
                    >
                      Value
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {parsedMessage.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={3} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No fields found in message
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ) : (
                    parsedMessage.map((field, index) => (
                      <TableRow
                        key={`${field.tag}-${index}`}
                        sx={{
                          "&:nth-of-type(odd)": {
                            backgroundColor: withAlpha(tokens.colors.defaultGridRowColor, 0.5),
                          },
                          "&:hover": {
                            backgroundColor: tokens.colors.defaultGridHoverColor,
                          },
                        }}
                      >
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            fontWeight: 600,
                            color: tokens.palette.primary,
                          }}
                        >
                          {field.tag}
                        </TableCell>
                        <TableCell sx={{ fontWeight: 500 }}>{field.tagName}</TableCell>
                        <TableCell
                          sx={{
                            fontFamily: "monospace",
                            wordBreak: "break-all",
                          }}
                        >
                          {field.displayValue}
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          {/* Timestamp info */}
          {timestamp && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="caption" color="textSecondary">
                Message Timestamp: {timestamp}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>
    </Modal>
  );
};

export default FixMessageDecoderModal;
